import { fetchCircuitPieceCatalog } from '../circuits/circuitPieceCatalogApi.js';
import type { CircuitPiece3dCatalogEntry } from '@multirx/shared/game/circuitPieceCatalog.js';
import { mountLazyCar3dPreview } from '../cars/car3dPreviewGrid.js';
import {
  ASSET_PICKER_BATCH_SIZE,
  attachAssetPickerScrollSentinel,
  type AssetPickerScrollSentinelHandle,
} from './assetPickerScroll.js';

export type CircuitPiecePickerOptions = {
  title: string;
  /**
   * Catégorie du catalogue à afficher. Si omise, la modale charge les deux
   * catégories et affiche des onglets "Pièces"/"Décor" pour basculer entre
   * elles (un seul bouton d'entrée côté éditeur, voir `circuit3dEditor.ts`) —
   * le clic sur un item reste piloté par son propre `item.category`, jamais
   * par l'onglet actif au moment du clic.
   */
  category?: 'road' | 'decor';
  /** Slug actuellement sélectionné, s'il y en a un. */
  currentSlug?: string | null;
  onSelect: (item: CircuitPiece3dCatalogEntry) => void;
  onError?: (message: string) => void;
  /** Appelé quand la modale se ferme, quelle que soit la façon (choix, bouton, Échap, clic dehors). */
  onClose?: () => void;
};

function closeDialog(dialog: HTMLDialogElement, cleanups: Array<() => void>): void {
  cleanups.forEach((fn) => fn());
  cleanups.length = 0;
  dialog.close();
  dialog.remove();
}

/**
 * Ouvre une modale pour choisir une pièce du catalogue Kenney (route ou décor
 * selon `options.category`). Réutilise le pattern du sélecteur de modèle 3D
 * voiture (`car3dModelPicker.ts`), y compris son aperçu 3D paresseux/limité en
 * concurrence (112 pièces au total dans le catalogue).
 */
export function openCircuitPiecePicker(options: CircuitPiecePickerOptions): void {
  const dialog = document.createElement('dialog');
  dialog.className = 'asset-picker-dialog';
  dialog.setAttribute('aria-labelledby', 'circuit-piece-picker-title');

  const inner = document.createElement('div');
  inner.className = 'asset-picker-dialog-inner';

  const head = document.createElement('div');
  head.className = 'asset-picker-head';
  head.innerHTML = `<h2 id="circuit-piece-picker-title" class="asset-picker-title">${options.title}</h2>`;

  const cleanups: Array<() => void> = [];
  let sentinelHandle: AssetPickerScrollSentinelHandle | null = null;
  const close = (): void => {
    sentinelHandle?.disconnect();
    sentinelHandle = null;
    closeDialog(dialog, cleanups);
    options.onClose?.();
  };

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'btn btn-ghost asset-picker-close';
  closeBtn.textContent = 'Fermer';
  closeBtn.addEventListener('click', close);
  head.appendChild(closeBtn);

  // Onglets Pièces/Décor uniquement quand `category` n'est pas imposée par
  // l'appelant (bouton combiné "Décor / Pièce" de l'éditeur 3D) — un appelant
  // qui fixe déjà `category` n'a pas besoin de basculer.
  const combined = options.category === undefined;
  let activeCategory: 'road' | 'decor' = options.category ?? 'decor';
  let tabs: { road: HTMLButtonElement; decor: HTMLButtonElement } | null = null;
  let tabRow: HTMLDivElement | null = null;
  if (combined) {
    tabRow = document.createElement('div');
    tabRow.className = 'circuit-piece-picker-tabs';
    tabRow.setAttribute('role', 'tablist');
    const roadTab = document.createElement('button');
    roadTab.type = 'button';
    roadTab.className = 'circuit-piece-picker-tab';
    roadTab.textContent = 'Pièces';
    roadTab.setAttribute('role', 'tab');
    const decorTab = document.createElement('button');
    decorTab.type = 'button';
    decorTab.className = 'circuit-piece-picker-tab';
    decorTab.textContent = 'Décor';
    decorTab.setAttribute('role', 'tab');
    tabs = { road: roadTab, decor: decorTab };
    roadTab.addEventListener('click', () => setActiveCategory('road'));
    decorTab.addEventListener('click', () => setActiveCategory('decor'));
    tabRow.append(roadTab, decorTab);
  }

  const search = document.createElement('input');
  search.type = 'search';
  search.className = 'asset-picker-search';
  search.setAttribute('aria-label', 'Filtrer le catalogue');

  const grid = document.createElement('div');
  // `circuit-piece-picker-grid` réduit la hauteur visible par rapport au sélecteur
  // de voiture : la scène 3D de l'éditeur reste ouverte derrière ce dialogue, donc
  // moins de vignettes 3D simultanément visibles = moins de contextes WebGL actifs
  // à la fois (le navigateur perd le plus ancien contexte au-delà d'un certain
  // nombre — sans cette limite, l'éditeur 3D lui-même se retrouvait coupé).
  grid.className = 'asset-picker-grid car3d-picker-grid circuit-piece-picker-grid';
  grid.setAttribute('role', 'listbox');

  const status = document.createElement('p');
  status.className = 'asset-picker-status';
  status.textContent = 'Chargement…';

  inner.append(head, ...(tabRow ? [tabRow] : []), search, status, grid);
  dialog.appendChild(inner);
  document.body.appendChild(dialog);

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) close();
  });
  dialog.addEventListener('cancel', close);

  let items: CircuitPiece3dCatalogEntry[] = [];
  let visibleCount = ASSET_PICKER_BATCH_SIZE;
  let filtered: CircuitPiece3dCatalogEntry[] = [];

  /** Libellés dépendant de la catégorie active (recherche, aria-label, onglets). */
  function updateHeadForCategory(): void {
    search.placeholder =
      activeCategory === 'decor' ? 'Rechercher un décor…' : 'Rechercher une pièce…';
    grid.setAttribute(
      'aria-label',
      activeCategory === 'decor' ? 'Décors disponibles' : 'Pièces de route disponibles'
    );
    if (!tabs) return;
    tabs.road.classList.toggle('is-active', activeCategory === 'road');
    tabs.road.setAttribute('aria-selected', String(activeCategory === 'road'));
    tabs.decor.classList.toggle('is-active', activeCategory === 'decor');
    tabs.decor.setAttribute('aria-selected', String(activeCategory === 'decor'));
  }
  updateHeadForCategory();

  function setActiveCategory(next: 'road' | 'decor'): void {
    if (activeCategory === next) return;
    activeCategory = next;
    visibleCount = ASSET_PICKER_BATCH_SIZE;
    updateHeadForCategory();
    renderGrid();
  }

  function createItemButton(item: CircuitPiece3dCatalogEntry): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'car3d-picker-item';
    if (item.slug === options.currentSlug) btn.classList.add('is-selected');
    btn.setAttribute('role', 'option');
    btn.title = `${item.displayName} (${item.slug})`;

    const previewWrap = document.createElement('div');
    previewWrap.className = 'car3d-picker-item-preview';
    if (item.cellCost > 1) {
      const badge = document.createElement('span');
      badge.className = 'circuit-piece-picker-cost-badge';
      badge.textContent = `${item.cellCost} cases`;
      previewWrap.appendChild(badge);
    }

    const cap = document.createElement('span');
    cap.className = 'asset-picker-item-name';
    cap.textContent = item.displayName;

    btn.append(previewWrap, cap);
    btn.addEventListener('click', () => {
      options.onSelect(item);
      close();
    });
    // `root: grid` est indispensable ici : la grille défile dans sa propre zone
    // (max-height + overflow-y), sans lui l'observateur compare à la fenêtre
    // entière et monte tous les aperçus 3D à la fois (contextes WebGL épuisés).
    cleanups.push(mountLazyCar3dPreview(previewWrap, item.publicPath, grid));
    return btn;
  }

  /** (Ré)attache la sentinelle de pagination après le dernier item affiché. */
  function attachSentinel(): void {
    sentinelHandle?.disconnect();
    sentinelHandle = attachAssetPickerScrollSentinel(
      grid,
      filtered.length > visibleCount,
      appendNextBatch
    );
  }

  /**
   * Ajoute le lot suivant SANS toucher aux items déjà affichés — contrairement
   * à un `renderGrid()` complet, qui détruisait et remontait TOUS les aperçus
   * 3D (y compris ceux déjà chargés) à chaque défilement. Avec la file
   * d'attente partagée de `mountLazyCar3dPreview` (4 chargements simultanés
   * max), ce ré-montage massif orphelinait les chargements en cours à chaque
   * lot révélé : leurs jetons de file ne se libéraient qu'à la fin du réseau,
   * bloquant les lots suivants derrière une file croissante de requêtes
   * devenues inutiles — d'où des vignettes qui ne finissaient jamais de
   * charger passé les deux premiers lots.
   */
  function appendNextBatch(): void {
    const next = filtered.slice(visibleCount, visibleCount + ASSET_PICKER_BATCH_SIZE);
    visibleCount += ASSET_PICKER_BATCH_SIZE;
    sentinelHandle?.disconnect();
    sentinelHandle = null;
    next.forEach((item) => grid.appendChild(createItemButton(item)));
    attachSentinel();
  }

  /** Reconstruction complète — uniquement quand l'ensemble filtré change
   * (catégorie, recherche, chargement initial), pas au défilement. */
  const renderGrid = (): void => {
    cleanups.forEach((fn) => fn());
    cleanups.length = 0;
    sentinelHandle?.disconnect();
    sentinelHandle = null;
    grid.innerHTML = '';

    const byCategory = combined ? items.filter((it) => it.category === activeCategory) : items;
    const q = search.value.trim().toLowerCase();
    filtered = q
      ? byCategory.filter(
          (it) => it.slug.toLowerCase().includes(q) || it.displayName.toLowerCase().includes(q)
        )
      : byCategory;

    if (filtered.length === 0 && q) {
      status.hidden = false;
      status.textContent = 'Aucune pièce ne correspond.';
    } else {
      status.hidden = true;
    }

    filtered.slice(0, visibleCount).forEach((item) => grid.appendChild(createItemButton(item)));
    attachSentinel();
  };

  search.addEventListener('input', () => {
    visibleCount = ASSET_PICKER_BATCH_SIZE;
    renderGrid();
  });

  dialog.showModal();

  void fetchCircuitPieceCatalog(options.category)
    .then((catalog) => {
      items = catalog;
      status.textContent = `${items.length} pièce(s)`;
      updateHeadForCategory();
      renderGrid();
    })
    .catch((err: Error) => {
      status.hidden = false;
      status.textContent = err.message || 'Impossible de charger le catalogue de pièces.';
      options.onError?.(status.textContent);
    });
}
